import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { token } from "@atlaskit/tokens";

import { analyticsLabels } from "@/components/global-nav/user-profile-new/src";
import { useLocalizedHref } from "@/libraries/web-framework";

import Link from "../../../../components/wpl/link/src/index";
import { GlobalNavLinkContentProps } from "../nav-links-container";

import { Divider } from "./icons/divider";
import { DropdownType } from "./mobile-nav";
import { MobileNavLink } from "./mobile-nav-link";

const linkContainerCss = css({
    listStyle: "none",
    padding: "0 0 24px 0",
    margin: "0",
    fontWeight: "700",
    fontSize: "14px"
});

const signInContainerCss = css({
    display: "flex",
    justifyContent: "center"
});

const signInCss = css({
    paddingBlock: token("space.200")
});

export type MobileNavLinkContentProps = {
    readonly globalLinksKey: string;
    readonly linkText: string;
    readonly hasSubnav: boolean;
    readonly href?: string;
    readonly productKey?: string;
    readonly dropdownType?: DropdownType;
};

type MobileNavLinksContainerInfo = {
    key: string;
    mobileLinksProps: MobileNavLinkContentProps[];
};

interface MobileNavLinksContainerProps extends MobileNavLinksContainerInfo {
    handleMenuPage: (menuPage: DropdownType) => void;
}

const MobileNavLinksContainer = ({
    handleMenuPage,
    key,
    mobileLinksProps
}: MobileNavLinksContainerProps) => {
    return (
        <>
            <ul className={key} css={linkContainerCss}>
                {mobileLinksProps.map(
                    (mobileLink: GlobalNavLinkContentProps) => {
                        return (
                            <li
                                className="nav-link"
                                key={mobileLink.globalLinksKey}
                            >
                                <MobileNavLink
                                    handleMenuPage={handleMenuPage}
                                    {...mobileLink}
                                />
                            </li>
                        );
                    }
                )}
            </ul>
            <Divider />
        </>
    );
};

type MobileNavLinksProps = {
    isLoggedIn: boolean;
    handleMenuPage: (menuPage: DropdownType) => void;
};

export const MobileNavLinks = ({
    isLoggedIn,
    handleMenuPage
}: MobileNavLinksProps) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const mobileLinksSections: MobileNavLinksContainerInfo[] = [
        {
            key: "product-nav-links",
            mobileLinksProps: [
                {
                    globalLinksKey: "products",
                    linkText: intl.formatMessage({
                        id: "globalnavv2.navlink.products",
                        defaultMessage: "Products"
                    }),
                    hasSubnav: false
                },
                {
                    globalLinksKey: "everyone",
                    linkText: intl.formatMessage({
                        id: "globalnavv2.navlink.everyone",
                        defaultMessage: "Everyone"
                    }),
                    hasSubnav: true,
                    dropdownType: "product-everyone"
                },
                {
                    globalLinksKey: "developers",
                    linkText: intl.formatMessage({
                        id: "globalnavv2.navlink.developers",
                        defaultMessage: "Developers"
                    }),
                    hasSubnav: true,
                    dropdownType: "product-developer"
                },
                {
                    globalLinksKey: "projectmanagers",
                    linkText: intl.formatMessage({
                        id: "globalnavv2.navlink.projectmanagers",
                        defaultMessage: "Project managers"
                    }),
                    hasSubnav: true,
                    dropdownType: "product-project-managers"
                },
                {
                    globalLinksKey: "itprofessionals",
                    linkText: intl.formatMessage({
                        id: "globalnavv2.navlinkitprofessionals",
                        defaultMessage: "IT professionals"
                    }),
                    hasSubnav: true,
                    dropdownType: "product-it-professionals"
                }
            ]
        },
        {
            key: "other-nav-links",
            mobileLinksProps: [
                {
                    globalLinksKey: "teams",
                    linkText: intl.formatMessage({
                        id: "globalnavv2.navlink.teams",
                        defaultMessage: "Teams"
                    }),
                    hasSubnav: true,
                    dropdownType: "teams"
                },
                {
                    globalLinksKey: "why-atlassian",
                    linkText: intl.formatMessage({
                        id: "globalnavv2.navlink.whyAtlassian",
                        defaultMessage: "Why Atlassian?"
                    }),
                    hasSubnav: true,
                    dropdownType: "why-atlassian"
                },
                {
                    globalLinksKey: "resources",
                    linkText: intl.formatMessage({
                        id: "globalnavv2.navlink.resources",
                        defaultMessage: "Resources"
                    }),
                    hasSubnav: true,
                    dropdownType: "resources"
                },
                {
                    globalLinksKey: "enterprise",
                    linkText: intl.formatMessage({
                        id: "globalnavv2.navlink.enterprise",
                        defaultMessage: "Enterprise"
                    }),
                    hasSubnav: false,
                    href: useLocalizedHref("/enterprise")
                }
            ]
        }
    ];

    const baseUrl = process.env["NEXT_PUBLIC_IDENTITY_BASE_URL"];
    const stargateBaseUrl = process.env["NEXT_PUBLIC_API_PRIVATE_BASE_URL"];
    const continueUrl = stargateBaseUrl + "/start/authredirect";
    const signInUrl = `${baseUrl}/login?continue=${encodeURIComponent(
        continueUrl
    )}`;

    const signinLink = {
        text: intl.formatMessage({
            id: "globalnavv2.navlink.signin",
            defaultMessage: "Sign in"
        }),
        href: signInUrl
    };

    const onLogInClick = () => {
        createAnalyticsEvent({
            action: "clicked",
            name: "bxp.globalnavdropdown.click.login",
            label: analyticsLabels["JoinSiteUserDropdown.logIn"]
        }).fire();
    };

    return (
        <div>
            {mobileLinksSections.map(({ key, mobileLinksProps }) => (
                <MobileNavLinksContainer
                    key={key}
                    mobileLinksProps={mobileLinksProps}
                    handleMenuPage={handleMenuPage}
                />
            ))}
            <div css={signInContainerCss}>
                {!isLoggedIn && (
                    <Link
                        key={signinLink.text}
                        text={signinLink.text}
                        href={signinLink.href}
                        type="basic"
                        css={signInCss}
                        doesArrowAnimate={true}
                        onClick={onLogInClick}
                    />
                )}
            </div>
        </div>
    );
};
